import { isNotDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { FormField } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/common/FormField.tsx';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import {
  selectForwardAccumulatorCustomFields,
  selectForwardAccumulatorProperty,
} from '@/store/forwardAccumulator/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';
import type { Frequency as FixingFrequencyType } from '@/types/common';

const FixingFrequency = () => {
  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const fixingFrequency = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'fixingFrequency'));
  const fixingFrequenciesList = useAppSelector((state) =>
    selectForwardAccumulatorProperty(state, tileId, 'fixingFrequenciesList'),
  );
  const numberOfFixings = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'numberOfFixings'));

  const isCustom = useAppSelector(
    (state) => selectForwardAccumulatorCustomFields(state, tileId)?.includes('FixingFrequency') ?? false,
  );

  const handleFixingFrequencies = (event: React.ChangeEvent<HTMLSelectElement>) => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Fixing frequency changed', event.target.value);
    return check({
      fixingFrequency: event.target.value as FixingFrequencyType,
    });
  };

  const handleNumberOfFixings = (value: number | undefined) => {
    if (isNotDefined(value)) {
      return;
    }

    return check({
      numberOfFixings: value,
    });
  };

  return (
    <FormField
      formattedMessageId="forwardAccumulator.form.fixingFrequencies"
      extra={
        <Tooltip position="right" contents={<FormattedMessage id="forwardAccumulator.form.fixingFrequency.info" />}>
          <div className="badge badge-dismissible">
            <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
          </div>
        </Tooltip>
      }
    >
      <div className="d-flex">
        <Form.Select
          value={fixingFrequency}
          onChange={handleFixingFrequencies}
          size="sm"
          data-e2e="fixing-frequencies-selector"
        >
          {fixingFrequenciesList?.map((mode) => (
            <option key={mode} value={mode}>
              <FormattedMessage id={`forwardAccumulator.form.fixingFrequencies.${mode}`} />
            </option>
          ))}
        </Form.Select>
        <div className="w-25 ps-1">
          <SgNumberInput
            valuePosition="start"
            selectAllOnFocus
            className={isCustom ? 'text-info' : undefined}
            value={numberOfFixings}
            maxDecimals={2}
            onChange={handleNumberOfFixings}
            size="sm"
            data-e2e="numberOfFixings"
          />
        </div>
      </div>
    </FormField>
  );
};

export default FixingFrequency;
